import React, { Fragment, useEffect, useState } from "react";
import api from "../../api";
import InlineCheckbox from "../../components/InlineCheckbox";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
} from "chart.js";
import { Bar, Pie } from "react-chartjs-2";
import Badge from "../../components/Badge";
import { useDarkTheme } from "../../context/themeContext";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import {
    ReportShell,
    ReportStatCard,
    ReportPanel,
} from "../../components/reports/ReportShell";
import {
    ReportDateFilterMenu,
    ReportComboboxFilterMenu,
    ReportCustomFilterMenu,
} from "../../components/reports/ReportFilters";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
);

const options = [
    { name: "Datos generales", href: "#" },
    { name: "Cirugias", href: "/reports/surgery-report" },
    { name: "Estudios", href: "/reports/study-report" },
    { name: "Medicamentos", href: "/reports/medication-report" },
];

export default function GeneralDataPage() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarOpenDesktop, setSidebarOpenDesktop] = useState(true);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState();
    const [diagnoses, setDiagnoses] = useState();
    const [doctors, setDoctors] = useState();
    const [filters, setFilters] = useState({});
    const [fromDate, setFromDate] = useState();
    const [toDate, setToDate] = useState();
    const [diagnosesFilter, setDiagnosesFilter] = useState();
    const [doctorFilter, setDoctorFilter] = useState();
    const [consultationTypeFilter, setConsultationTypeFilter] = useState();
    const { isDark } = useDarkTheme();
    const isDesktop = useMediaQuery("(min-width: 1024px)");

    const handleFromDate = (field, value) => setFromDate(value);
    const handleToDate = (field, value) => setToDate(value);
    const handleDiagnosesFilter = (field, value) => setDiagnosesFilter(value);
    const handleDoctorFilter = (field, value) => setDoctorFilter(value);
    const handleConsultationTypeFilter = (field, value) => {
        if (value) {
            const next = consultationTypeFilter ? [...consultationTypeFilter] : [];
            next.push(field);
            setConsultationTypeFilter(next);
            return;
        }

        const next = (consultationTypeFilter || []).filter(
            (element) => field !== element,
        );
        setConsultationTypeFilter(next);
    };

    useEffect(() => {
        if (isDesktop) {
            setSidebarOpen(false);
        }
    }, [isDesktop]);

    useEffect(() => {
        api.diagnostics.getMulti().then((response) => setDiagnoses(response));
        api.users.getDoctors().then((response) => {
            setDoctors(
                response.map((element) => {
                    const isLicenciado =
                        element.role.toLowerCase() === "licenciado";
                    const prefix = isLicenciado
                        ? "Lic."
                        : element.sex.toLowerCase() === "masculino"
                          ? "Dr."
                          : "Dra.";

                    return {
                        name: `${prefix} ${element.firstName} ${element.lastName}`,
                        ...element,
                    };
                }),
            );
        });
    }, []);

    useEffect(() => {
        setLoading(true);
        const aux = new URLSearchParams();

        Object.keys(filters || {}).forEach((prop) => {
            const value = filters[prop];
            if (Array.isArray(value)) {
                value.forEach((element) =>
                    aux.append(prop, element.id ? element.id : element),
                );
            } else if (value !== undefined && value !== null) {
                aux.append(prop, value);
            }
        });

        const request =
            aux.toString().length > 0
                ? api.generalData.getReports(aux)
                : api.generalData.getReports();

        request
            .then((response) => setData(response))
            .finally(() => setLoading(false));
    }, [filters]);

    const saveFromDate = () => {
        if (fromDate) setFilters((prev) => ({ ...prev, date_after: fromDate }));
    };

    const saveToDate = () => {
        if (toDate) setFilters((prev) => ({ ...prev, date_before: toDate }));
    };

    const saveDiagnosesFilter = () => {
        if (diagnosesFilter) {
            setFilters((prev) => ({ ...prev, diagnostics: diagnosesFilter }));
        }
    };

    const saveDoctorFilter = () => {
        if (doctorFilter) {
            setFilters((prev) => ({ ...prev, doctor: doctorFilter }));
        }
    };

    const saveConsultationTypeFilter = () => {
        if (consultationTypeFilter?.length) {
            setFilters((prev) => ({
                ...prev,
                consultation_type: consultationTypeFilter,
            }));
        }
    };

    const deleteFromDate = () => {
        const state = { ...filters };
        delete state.date_after;
        setFilters(state);
        setFromDate(undefined);
    };

    const deleteToDate = () => {
        const state = { ...filters };
        delete state.date_before;
        setFilters(state);
        setToDate(undefined);
    };

    const deleteDates = () => {
        const state = { ...filters };
        delete state.date_after;
        delete state.date_before;
        setFilters(state);
        setFromDate(undefined);
        setToDate(undefined);
    };

    const deleteDiagnosesFilter = (value) => {
        setFilters((prev) => ({
            ...prev,
            diagnostics: (prev.diagnostics || []).filter(
                (element) => element.id !== value,
            ),
        }));
        setDiagnosesFilter((prev) =>
            (prev || []).filter((element) => element.id !== value),
        );
    };

    const deleteAllDiagnosesFilter = () => {
        const state = { ...filters };
        delete state.diagnostics;
        setFilters(state);
        setDiagnosesFilter([]);
    };

    const deleteDoctorFilter = (value) => {
        setFilters((prev) => ({
            ...prev,
            doctor: (prev.doctor || []).filter((element) => element.id !== value),
        }));
        setDoctorFilter((prev) =>
            (prev || []).filter((element) => element.id !== value),
        );
    };

    const deleteAllDoctorFilter = () => {
        const state = { ...filters };
        delete state.doctor;
        setFilters(state);
        setDoctorFilter([]);
    };

    const deleteAllConsultationTypeFilter = () => {
        const state = { ...filters };
        delete state.consultation_type;
        setFilters(state);
        setConsultationTypeFilter([]);
    };

    const textColor = isDark ? "#d1d5db" : "#374151";
    const gridColor = isDark ? "#4b5563" : "#e5e7eb";

    const barData = data
        ? {
              labels: data.charts.bar.data.map((element) => element.date),
              datasets: [
                  {
                      label: "Pacientes nuevos",
                      data: data.charts.bar.data.map(
                          (element) => element.newPatients,
                      ),
                      backgroundColor: "#38bdf8",
                  },
                  {
                      label: "Pacientes atendidos",
                      data: data.charts.bar.data.map(
                          (element) => element.patientsAttended,
                      ),
                      backgroundColor: "#14b8a6",
                  },
                  {
                      label: "Consultas registradas",
                      data: data.charts.bar.data.map(
                          (element) => element.totalConsultations,
                      ),
                      backgroundColor: "#fb7185",
                  },
              ],
          }
        : undefined;

    const pieTotal = (data?.charts?.pie?.men || 0) + (data?.charts?.pie?.women || 0);
    const pieData = data
        ? {
              labels: [
                  `Hombres: ${
                      pieTotal
                          ? Math.round((data.charts.pie.men / pieTotal) * 100)
                          : 0
                  }%`,
                  `Mujeres: ${
                      pieTotal
                          ? Math.round((data.charts.pie.women / pieTotal) * 100)
                          : 0
                  }%`,
              ],
              datasets: [
                  {
                      data: [data.charts.pie.men, data.charts.pie.women],
                      backgroundColor: ["#60a5fa", "#fb7185"],
                  },
              ],
          }
        : undefined;

    const optionsBar = {
        plugins: {
            legend: {
                labels: {
                    color: textColor,
                },
            },
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                ticks: { color: textColor },
                grid: { color: gridColor },
            },
            y: {
                ticks: { color: textColor },
                grid: { color: gridColor },
            },
        },
    };

    const optionsPie = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: isDesktop ? "bottom" : "bottom",
                labels: { color: textColor },
            },
        },
    };

    const summaryData = data?.summaryData;
    const men = data?.charts?.pie?.men || 0;
    const women = data?.charts?.pie?.women || 0;
    const attended = summaryData?.patientsAttended || 0;
    const newPatients = summaryData?.newPatients || 0;
    const totalConsultations = summaryData?.totalConsultations || 0;
    const patientsWithAnyCondition = Math.max(
        0,
        (summaryData?.dmPatients || 0) +
            (summaryData?.htaPatients || 0) -
            (summaryData?.dmAndHtaPatients || 0),
    );

    const summaryCards = [
        <ReportStatCard
            key="attended"
            label="Pacientes atendidos"
            value={attended}
            tone="blue"
            hint="Total de pacientes alcanzados en el periodo."
        />,
        <ReportStatCard
            key="new"
            label="Pacientes nuevos"
            value={newPatients}
            tone="emerald"
            hint={
                attended
                    ? `${Math.round((newPatients / attended) * 100)}% del total atendido.`
                    : "Sin datos para calcular proporcion."
            }
        />,
        <ReportStatCard
            key="consultations"
            label="Consultas registradas"
            value={totalConsultations}
            tone="rose"
            hint={
                attended
                    ? `${(totalConsultations / attended).toFixed(1)} consultas por paciente.`
                    : "Promedio no disponible."
            }
        />,
        <ReportStatCard
            key="conditions"
            label="Pacientes con DM o HTA"
            value={patientsWithAnyCondition}
            tone="amber"
            hint="Pacientes con al menos una comorbilidad relevante."
        />,
    ];

    return (
        <ReportShell
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            sidebarOpenDesktop={sidebarOpenDesktop}
            setSidebarOpenDesktop={setSidebarOpenDesktop}
            isDesktop={isDesktop}
            loading={loading}
            currentLabel="Datos generales"
            options={options}
            title="Panorama general de pacientes y consultas"
            actions={
                <>
                    <ReportDateFilterMenu
                        handleFromDate={handleFromDate}
                        handleToDate={handleToDate}
                        onClear={deleteDates}
                        onApply={() => {
                            saveToDate();
                            saveFromDate();
                        }}
                    />
                    <ReportComboboxFilterMenu
                        label="Diagnostico"
                        comboboxLabel="Diagnostico"
                        options={diagnoses}
                        handleChange={handleDiagnosesFilter}
                        name="diagnoses"
                        value={diagnosesFilter}
                        onClear={deleteAllDiagnosesFilter}
                        onApply={saveDiagnosesFilter}
                    />
                    <ReportComboboxFilterMenu
                        label="Doctor"
                        comboboxLabel="Doctor"
                        options={doctors}
                        handleChange={handleDoctorFilter}
                        name="doctors"
                        onClear={deleteAllDoctorFilter}
                        onApply={saveDoctorFilter}
                    />
                    <ReportCustomFilterMenu
                        label="Tipo de consulta"
                        onClear={deleteAllConsultationTypeFilter}
                        onApply={saveConsultationTypeFilter}
                    >
                        <p>General</p>
                        <InlineCheckbox handleChange={handleConsultationTypeFilter} name={1} label="Control por tratamiento" />
                        <InlineCheckbox handleChange={handleConsultationTypeFilter} name={2} label="Control post quirurgico" />
                        <InlineCheckbox handleChange={handleConsultationTypeFilter} name={3} label="Estudios" />
                        <p>De especialidad</p>
                        <InlineCheckbox handleChange={handleConsultationTypeFilter} name={4} label="Control por tratamiento" />
                        <InlineCheckbox handleChange={handleConsultationTypeFilter} name={5} label="Control post quirurgico" />
                        <InlineCheckbox handleChange={handleConsultationTypeFilter} name={6} label="Estudios" />
                    </ReportCustomFilterMenu>
                </>
            }
            badges={
                <>
                    {filters.date_after && <Badge text={`Desde: ${filters.date_after}`} handleDelete={deleteFromDate} />}
                    {filters.date_before && <Badge text={`Hasta: ${filters.date_before}`} handleDelete={deleteToDate} />}
                    {(filters.diagnostics || []).map((element) => (
                        <Badge key={element.id} text={element.name} handleDelete={() => deleteDiagnosesFilter(element.id)} />
                    ))}
                    {(filters.doctor || []).map((element) => (
                        <Badge key={element.id} text={element.name} handleDelete={() => deleteDoctorFilter(element.id)} />
                    ))}
                </>
            }
            summary={summaryCards}
        >
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(340px,1fr)]">
                <ReportPanel title="Evolucion">
                    {barData ? (
                        <div className="h-[320px] sm:h-[380px]">
                            <Bar options={optionsBar} data={barData} />
                        </div>
                    ) : null}
                </ReportPanel>

                <ReportPanel title="Sexo">
                    {pieData ? (
                        <div className="mx-auto h-[320px] max-w-md sm:h-[380px]">
                            <Pie options={optionsPie} data={pieData} />
                        </div>
                    ) : null}
                </ReportPanel>
            </div>

            <ReportPanel title="Indicadores">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    <ReportStatCard label="Pacientes con DM" value={summaryData?.dmPatients || 0} tone="violet" />
                    <ReportStatCard label="Pacientes con HTA" value={summaryData?.htaPatients || 0} tone="rose" />
                    <ReportStatCard label="Pacientes con DM y HTA" value={summaryData?.dmAndHtaPatients || 0} tone="amber" />
                    <ReportStatCard label="Participacion masculina" value={pieTotal ? `${Math.round((men / pieTotal) * 100)}%` : "0%"} tone="blue" />
                    <ReportStatCard label="Participacion femenina" value={pieTotal ? `${Math.round((women / pieTotal) * 100)}%` : "0%"} tone="emerald" />
                    <ReportStatCard label="Consultas por paciente" value={attended ? (totalConsultations / attended).toFixed(1) : "0.0"} tone="slate" />
                </div>
            </ReportPanel>
        </ReportShell>
    );
}
